import { useEffect, useRef, useState, useCallback } from "react";
import { Canvas as FabricCanvas, Text, Rect, Circle, Line, Image } from "fabric";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLabelSettings } from "@/contexts/LabelSettingsContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { generateBarcodeDataUrl } from "@/utils/barcodeGenerator";
import { 
  Type, Square, Circle as CircleIcon, Minus, BarChart3, Trash2, 
  Copy, Download, Upload, Save, Plus, Star, StarOff, Eye,
  AlignLeft, AlignCenter, AlignRight, Grid, ZoomIn, ZoomOut,
  Bold, Italic, Underline
} from "lucide-react";

interface TemplateDesignerProps {
  onBack: () => void;
}

interface LabelTemplate {
  id: string;
  name: string;
  description?: string;
  is_default: boolean;
  category: string;
  dimensions: any;
  canvas_data: any;
  data_mappings: any;
  thumbnail?: string;
  created_at: string;
  updated_at: string;
}

type DataSourceType = "BARCODE" | "NAME" | "RETAIL_PRICE" | "CATEGORY" | "STATIC";

const DATA_SOURCES: { value: DataSourceType; label: string }[] = [
  { value: "STATIC", label: "Static Text" },
  { value: "BARCODE", label: "Product Barcode" },
  { value: "NAME", label: "Product Name" },
  { value: "RETAIL_PRICE", label: "Retail Price" },
  { value: "CATEGORY", label: "Category" },
];

const TEMPLATE_CATEGORIES = [
  { value: "receipt", label: "Receipt Label" },
  { value: "shelf", label: "Shelf Label" },
  { value: "price", label: "Price Tag" },
  { value: "barcode", label: "Barcode Label" },
  { value: "custom", label: "Custom" }
];

const SAMPLE_PRODUCT = {
  name: "Sample Product Name",
  barcode: "1234567890123",
  retail_price: 19.99,
  category: "Electronics"
};

export const TemplateDesigner = ({ onBack }: TemplateDesignerProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [fabricCanvas, setFabricCanvas] = useState<FabricCanvas | null>(null);
  const [selectedObject, setSelectedObject] = useState<any>(null);
  const [templates, setTemplates] = useState<LabelTemplate[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<LabelTemplate[]>([]);
  const [currentTemplate, setCurrentTemplate] = useState<LabelTemplate | null>(null);
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [templateCategory, setTemplateCategory] = useState("custom");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [zoom, setZoom] = useState(1);
  const [showGrid, setShowGrid] = useState(true);
  const [undoStack, setUndoStack] = useState<any[]>([]);
  const [redoStack, setRedoStack] = useState<any[]>([]);
  const [labelDimensions, setLabelDimensions] = useState({
    width: 2.4,
    height: 1.1,
    unit: 'in' as 'in' | 'mm'
  });

  // Helper function to convert dimensions to pixels
  const getDimensionsInPixels = () => {
    const WEB_DPI = 96; // Standard web DPI
    const pixelsPerInch = WEB_DPI;
    const pixelsPerMm = WEB_DPI / 25.4;
    
    const multiplier = labelDimensions.unit === 'in' ? pixelsPerInch : pixelsPerMm;
    
    // Ensure consistent canvas sizing that matches print proportions
    const width = Math.round(labelDimensions.width * multiplier);
    const height = Math.round(labelDimensions.height * multiplier);
    
    console.log(`Canvas dimensions: ${width}x${height}px for ${labelDimensions.width}x${labelDimensions.height}${labelDimensions.unit}`);
    
    return { width, height };
  };

  // Load templates on mount
  useEffect(() => {
    loadTemplates();
  }, []);

  // Filter templates based on search and category
  useEffect(() => {
    let filtered = templates;
    
    if (searchTerm) {
      filtered = filtered.filter(t => 
        t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (categoryFilter !== "all") {
      filtered = filtered.filter(t => t.category === categoryFilter);
    }
    
    setFilteredTemplates(filtered);
  }, [templates, searchTerm, categoryFilter]);

  // Initialize canvas
  useEffect(() => {
    if (!canvasRef.current) return;

    const pixelDimensions = getDimensionsInPixels();
    const canvas = new FabricCanvas(canvasRef.current, {
      width: pixelDimensions.width,
      height: pixelDimensions.height,
      backgroundColor: "#ffffff",
      preserveObjectStacking: true,
    });

    // Enhanced event handlers
    canvas.on('selection:created', (e) => {
      setSelectedObject(e.selected?.[0]);
      saveState();
    });
    canvas.on('selection:updated', (e) => {
      setSelectedObject(e.selected?.[0]);
      saveState();
    });
    canvas.on('selection:cleared', () => setSelectedObject(null));
    
    // Enhanced object:modified handler with position locking and boundary constraints
    
    // Position locking during scaling/resizing
    canvas.on('object:scaling', (e: any) => {
      const obj = e.target;
      if (!obj || !(obj as any).lockPosition) return;
      
      // Store original position during scaling
      (obj as any)._originalLeft = obj.left;
      (obj as any)._originalTop = obj.top;
    });
    
    canvas.on('object:modified', (e: any) => {
      const obj = e.target;
      if (!obj) return;
      
      // Handle position locking after scaling
      if ((obj as any).lockPosition && (obj as any)._originalLeft !== undefined && (obj as any)._originalTop !== undefined) {
        obj.set({
          left: (obj as any)._originalLeft,
          top: (obj as any)._originalTop
        });
        delete (obj as any)._originalLeft;
        delete (obj as any)._originalTop;
        canvas.renderAll();
        return;
      }
      
      // Apply boundary constraints
      const canvasWidth = canvas.width || 400;
      const canvasHeight = canvas.height || 300;
      
      // Keep objects within canvas bounds
      const objBounds = obj.getBoundingRect();
      
      if (objBounds.left < 0) {
        obj.set('left', obj.left - objBounds.left);
      }
      if (objBounds.top < 0) {
        obj.set('top', obj.top - objBounds.top);
      }
      if (objBounds.left + objBounds.width > canvasWidth) {
        obj.set('left', obj.left - (objBounds.left + objBounds.width - canvasWidth));
      }
      if (objBounds.top + objBounds.height > canvasHeight) {
        obj.set('top', obj.top - (objBounds.top + objBounds.height - canvasHeight));
      }
      
      // Text boundary constraints
      if (obj instanceof Text && (obj as any).textBoundary) {
        const boundary = (obj as any).textBoundary;
        const textWidth = obj.getScaledWidth();
        const textHeight = obj.getScaledHeight();
        
        // Keep text within defined boundary
        if (obj.left < boundary.left) obj.set('left', boundary.left);
        if (obj.top < boundary.top) obj.set('top', boundary.top);
        if (obj.left + textWidth > boundary.left + boundary.width) {
          obj.set('left', boundary.left + boundary.width - textWidth);
        }
        if (obj.top + textHeight > boundary.top + boundary.height) {
          obj.set('top', boundary.top + boundary.height - textHeight);
        }
      }
      
      canvas.renderAll();
      saveState();
    });
    
    canvas.on('object:added', () => saveState());
    canvas.on('object:removed', () => saveState());

    // Keyboard shortcuts
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 's':
            e.preventDefault();
            saveTemplate();
            break;
          case 'z':
            e.preventDefault();
            if (e.shiftKey) {
              redo();
            } else {
              undo();
            }
            break;
          case 'c':
            if (selectedObject) {
              e.preventDefault();
              duplicateSelectedObject();
            }
            break;
        }
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && selectedObject) {
        deleteSelectedElement();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    setFabricCanvas(canvas);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      canvas.dispose();
    };
  }, []);

  // Update canvas dimensions when label dimensions change
  useEffect(() => {
    if (fabricCanvas) {
      const pixelDimensions = getDimensionsInPixels();
      fabricCanvas.setDimensions(pixelDimensions);
      fabricCanvas.renderAll();
    }
  }, [fabricCanvas, labelDimensions]);

  const saveState = useCallback(() => {
    if (!fabricCanvas) return;
    
    const state = JSON.stringify(fabricCanvas.toJSON());
    setUndoStack(prev => [...prev.slice(-19), state]); // Keep last 20 states
    setRedoStack([]); // Clear redo stack on new action
  }, [fabricCanvas]);

  const undo = useCallback(() => {
    if (undoStack.length === 0 || !fabricCanvas) return;
    
    const currentState = JSON.stringify(fabricCanvas.toJSON());
    const previousState = undoStack[undoStack.length - 1];
    
    setRedoStack(prev => [...prev, currentState]);
    setUndoStack(prev => prev.slice(0, -1));
    
    fabricCanvas.loadFromJSON(previousState, () => {
      fabricCanvas.renderAll();
    });
  }, [fabricCanvas, undoStack]);

  const redo = useCallback(() => {
    if (redoStack.length === 0 || !fabricCanvas) return;
    
    const currentState = JSON.stringify(fabricCanvas.toJSON());
    const nextState = redoStack[redoStack.length - 1];
    
    setUndoStack(prev => [...prev, currentState]);
    setRedoStack(prev => prev.slice(0, -1));
    
    fabricCanvas.loadFromJSON(nextState, () => {
      fabricCanvas.renderAll();
    });
  }, [fabricCanvas, redoStack]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('label_templates')
        .select('*')
        .order('updated_at', { ascending: false });

      if (error) throw error;
      
      const templatesWithDefaults = (data || []).map((template: any) => ({
        ...template,
        category: template.category || 'custom',
        description: template.description || '',
        thumbnail: template.thumbnail || ''
      }));
      
      setTemplates(templatesWithDefaults);

      // Auto-load default template
      const defaultTemplate = templatesWithDefaults.find(t => t.is_default);
      if (defaultTemplate) {
        loadTemplate(defaultTemplate);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplate = async (template: LabelTemplate) => {
    if (!fabricCanvas) return;

    setLoading(true);
    try {
      console.log('Loading template:', template.name, template.canvas_data);
      
      setCurrentTemplate(template);
      setTemplateName(template.name);
      setTemplateDescription(template.description || '');
      setTemplateCategory(template.category || 'custom');
      
      // Set label dimensions from template
      if (template.dimensions) {
        setLabelDimensions({
          width: template.dimensions.width || 2.4,
          height: template.dimensions.height || 1.1,
          unit: template.dimensions.unit || 'in'
        });
      }
      
      fabricCanvas.clear();
      fabricCanvas.backgroundColor = "#ffffff";
      
      if (template.canvas_data?.objects && template.canvas_data.objects.length > 0) {
        console.log('Template has canvas data, loading objects:', template.canvas_data.objects.length);
        
        try {
          // Clear canvas first
          fabricCanvas.clear();
          fabricCanvas.backgroundColor = template.canvas_data.background || "#ffffff";
          
          // Load the JSON directly with better error handling
          fabricCanvas.loadFromJSON(template.canvas_data, () => {
            console.log('Canvas loaded successfully');
            
            // Process all objects to ensure proper data mapping
            const objects = fabricCanvas.getObjects();
            console.log('Found objects after loading:', objects.length);
            
            objects.forEach((obj: any, index) => {
              const originalData = template.canvas_data.objects[index];
              
              // Restore or set data properties for text elements
              if (obj.type === 'text') {
                // Ensure data source is set
                if (originalData?.dataSource && !obj.dataSource) {
                  obj.dataSource = originalData.dataSource;
                }
                if (originalData?.data && !obj.data) {
                  obj.set('data', originalData.data);
                }
                
                // Fix text stroke properties for Fabric.js v6
                if (obj.stroke) {
                  obj.set({
                    stroke: obj.stroke,
                    strokeWidth: obj.strokeWidth || Math.max(0.5, (obj.fontSize || 20) * 0.05)
                  });
                }
              }
              
              // Ensure object is interactive
              obj.set({
                selectable: true,
                evented: true,
                hasControls: true,
                hasBorders: true
              });
            });
            
            fabricCanvas.renderAll();
            updateTemplatePreview();
            saveState();
          });
        } catch (loadError) {
          console.error('Error loading template canvas data:', loadError);
          toast.error('Failed to load template design. Creating default elements.');
          createDefaultElements();
        }
      } else {
        console.log('No canvas data found, creating default elements');
        createDefaultElements();
      }
      
      toast.success(`Template "${template.name}" loaded`);
    } catch (error) {
      console.error('Error loading template:', error);
      toast.error('Failed to load template. Creating default elements instead.');
      // Fallback to default elements if template loading fails
      createDefaultElements();
    } finally {
      setLoading(false);
    }
  };

  const createDefaultElements = () => {
    if (!fabricCanvas) return;

    const elements = [
      { type: "NAME", text: "Product Name", x: 20, y: 20, fontSize: 16, fill: '#000000' },
      { type: "BARCODE", text: "[BARCODE]", x: 20, y: 50, fontSize: 12, fill: '#000000' },
      { type: "RETAIL_PRICE", text: "$0.00", x: 20, y: 80, fontSize: 14, fill: '#000000' },
      { type: "CATEGORY", text: "Category", x: 20, y: 110, fontSize: 12, fill: '#666666' }
    ];

    elements.forEach(element => {
      const textObj = new Text(element.text, {
        left: element.x,
        top: element.y,
        fontSize: element.fontSize,
        fontFamily: 'Arial',
        fill: element.fill,
        data: { sourceType: element.type },
        selectable: true,
        evented: true,
        hasControls: true,
        hasBorders: true
      });
      fabricCanvas.add(textObj);
    });

    fabricCanvas.renderAll();
    updateTemplatePreview();
    saveState();
  };

  const updateTemplatePreview = async () => {
    if (!fabricCanvas) return;

    console.log('Updating template preview...');
    
    const objects = fabricCanvas.getObjects();
    
    for (let obj of objects) {
      if (obj instanceof Text && (obj as any).data?.sourceType) {
        const sourceType = (obj as any).data.sourceType as DataSourceType;
        
        switch (sourceType) {
          case "NAME":
            obj.set('text', SAMPLE_PRODUCT.name);
            break;
          case "RETAIL_PRICE":
            obj.set('text', `$${SAMPLE_PRODUCT.retail_price.toFixed(2)}`);
            break;
          case "CATEGORY":
            obj.set('text', SAMPLE_PRODUCT.category);
            break;
          case "BARCODE":
            obj.set('text', SAMPLE_PRODUCT.barcode);
            break;
          case "STATIC":
            // Keep original text for static elements
            break;
        }
      }
      
      // Enhanced barcode image handling with proper async loading
      if ((obj as any).data?.elementType === 'barcode') {
        try {
          console.log('Updating barcode element:', obj);
          const barcodeDataUrl = generateBarcodeDataUrl(SAMPLE_PRODUCT.barcode, {
            width: 2,
            height: 60,
            displayValue: true
          });
          
          if (barcodeDataUrl) {
            // Remove old barcode object and create new one
            const oldLeft = obj.left;
            const oldTop = obj.top;
            const oldScaleX = obj.scaleX;
            const oldScaleY = obj.scaleY;
            const oldData = (obj as any).data;
            
            fabricCanvas.remove(obj);
            
            // Create new barcode image
            const newImg = await Image.fromURL(barcodeDataUrl);
            newImg.set({
              left: oldLeft,
              top: oldTop,
              scaleX: oldScaleX,
              scaleY: oldScaleY,
              data: oldData,
              selectable: true,
              evented: true,
              hasControls: true,
              hasBorders: true
            });
            
            fabricCanvas.add(newImg);
            console.log('Barcode updated successfully');
          }
        } catch (error) {
          console.error('Error updating barcode preview:', error);
        }
      }
    }

    fabricCanvas.renderAll();
  };

  const addTextElement = () => {
    if (!fabricCanvas) return;

    const textObj = new Text('New Text', {
      left: 50,
      top: 50,
      fontSize: 14,
      fontFamily: 'Arial',
      fill: '#000000',
      data: { sourceType: "STATIC" },
      selectable: true,
      evented: true,
      hasControls: true,
      hasBorders: true
    });

    fabricCanvas.add(textObj);
    fabricCanvas.setActiveObject(textObj);
    fabricCanvas.renderAll();
    toast("Text element added");
  };

  const addRectangle = () => {
    if (!fabricCanvas) return;

    const rect = new Rect({
      left: 100,
      top: 100,
      width: 100,
      height: 50,
      fill: 'transparent',
      stroke: '#000000',
      strokeWidth: 1,
      selectable: true,
      evented: true,
      hasControls: true,
      hasBorders: true
    });

    fabricCanvas.add(rect);
    fabricCanvas.setActiveObject(rect);
    fabricCanvas.renderAll();
    toast("Rectangle added");
  };

  const addCircle = () => {
    if (!fabricCanvas) return;

    const circle = new Circle({
      left: 100,
      top: 100,
      radius: 30,
      fill: 'transparent',
      stroke: '#000000',
      strokeWidth: 1,
      selectable: true,
      evented: true,
      hasControls: true,
      hasBorders: true
    });

    fabricCanvas.add(circle);
    fabricCanvas.setActiveObject(circle);
    fabricCanvas.renderAll();
    toast("Circle added");
  };

  const addLine = () => {
    if (!fabricCanvas) return;

    const line = new Line([50, 50, 150, 50], {
      stroke: '#000000',
      strokeWidth: 2,
      selectable: true,
      evented: true,
      hasControls: true,
      hasBorders: true
    });

    fabricCanvas.add(line);
    fabricCanvas.setActiveObject(line);
    fabricCanvas.renderAll();
    toast("Line added");
  };

  const addBarcodeElement = async () => {
    if (!fabricCanvas) return;

    try {
      const sampleBarcode = "1234567890123";
      const barcodeDataUrl = generateBarcodeDataUrl(sampleBarcode, {
        width: 2,
        height: 60,
        displayValue: true
      });

      if (barcodeDataUrl) {
        const img = await Image.fromURL(barcodeDataUrl);
        img.set({
          left: 50,
          top: 50,
          scaleX: 0.8,
          scaleY: 0.8,
          data: { sourceType: 'BARCODE', elementType: 'barcode' },
          selectable: true,
          evented: true,
          hasControls: true,
          hasBorders: true
        });

        fabricCanvas.add(img);
        fabricCanvas.setActiveObject(img);
        fabricCanvas.renderAll();
        toast("Barcode element added");
      } else {
        toast.error("Failed to generate barcode");
      }
    } catch (error) {
      console.error('Error adding barcode:', error);
      toast.error("Failed to add barcode element");
    }
  };

  const duplicateSelectedObject = async () => {
    if (!selectedObject || !fabricCanvas) return;

    try {
      const cloned = await selectedObject.clone();
      cloned.set({
        left: selectedObject.left + 20,
        top: selectedObject.top + 20,
        data: selectedObject.data ? { ...selectedObject.data } : undefined
      });
      fabricCanvas.add(cloned);
      fabricCanvas.setActiveObject(cloned);
      fabricCanvas.renderAll();
      toast("Element duplicated");
    } catch (error) {
      console.error('Error duplicating object:', error);
      toast.error("Failed to duplicate element");
    }
  };

  const deleteSelectedElement = () => {
    if (!selectedObject || !fabricCanvas) return;

    fabricCanvas.remove(selectedObject);
    setSelectedObject(null);
    fabricCanvas.renderAll();
    toast("Element deleted");
  };

  const updateSelectedObjectProperty = (property: string, value: any) => {
    if (!selectedObject || !fabricCanvas) return;

    try {
      if (selectedObject instanceof Text) {
        if (property === 'fontSize') {
          const newFontSize = Math.max(8, Math.min(72, parseInt(value) || 14));
          selectedObject.set('fontSize', newFontSize);
          
          // Auto-adjust stroke width proportionally
          if (selectedObject.stroke && selectedObject.strokeWidth && selectedObject.strokeWidth > 0) {
            const newStrokeWidth = Math.max(0.5, newFontSize * 0.05);
            selectedObject.set('strokeWidth', newStrokeWidth);
          }
        } else if (property === 'stroke') {
          selectedObject.set('stroke', value || null);
          if (value && (!selectedObject.strokeWidth || selectedObject.strokeWidth <= 0)) {
            selectedObject.set('strokeWidth', Math.max(0.5, (selectedObject.fontSize || 20) * 0.05));
          }
        } else if (property === 'strokeWidth') {
          selectedObject.set('strokeWidth', Math.max(0, parseFloat(value) || 0));
        } else if (property === 'sourceType') {
          const currentData = (selectedObject as any).data || {};
          selectedObject.set('data', { ...currentData, sourceType: value });
          updateTemplatePreview();
        } else if (property === 'text') {
          selectedObject.set('text', value);
        } else {
          selectedObject.set(property, value);
        }
      } else {
        // Handle other object types
        if (property === 'strokeWidth') {
          selectedObject.set('strokeWidth', Math.max(0, parseInt(value) || 0));
        } else if (property === 'radius' && selectedObject instanceof Circle) {
          selectedObject.set('radius', Math.max(1, parseInt(value) || 30));
        } else {
          selectedObject.set(property, value);
        }
      }
      
      fabricCanvas.renderAll();
      setSelectedObject({ ...selectedObject }); // Force re-render
    } catch (error) {
      console.error('Error updating object property:', error);
      toast.error('Failed to update property');
    }
  };

  const alignObject = (alignment: 'left' | 'center' | 'right') => {
    if (!selectedObject || !fabricCanvas) return;

    const canvasWidth = fabricCanvas.width || 400;
    const objWidth = selectedObject.getScaledWidth();

    let newLeft: number;
    switch (alignment) {
      case 'left':
        newLeft = 10;
        break;
      case 'center':
        newLeft = (canvasWidth - objWidth) / 2;
        break;
      case 'right':
        newLeft = canvasWidth - objWidth - 10;
        break;
    }

    selectedObject.set('left', newLeft);
    fabricCanvas.renderAll();
    toast(`Element aligned ${alignment}`);
  };

  const generateThumbnail = (): string => {
    if (!fabricCanvas) return '';
    
    return fabricCanvas.toDataURL({
      format: 'png',
      quality: 0.8,
      multiplier: 0.3
    });
  };

  const saveTemplate = async () => {
    if (!fabricCanvas || !templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    setSaving(true);
    try {
      // Enhanced canvas data serialization with proper data preservation
      const canvasData = fabricCanvas.toJSON();
      
      // Ensure all objects have their data properties preserved
      canvasData.objects = canvasData.objects.map((obj: any, index: number) => {
        const fabricObj = fabricCanvas.getObjects()[index];
        return {
          ...obj,
          data: (fabricObj as any).data || obj.data || {}
        };
      });
      
      // Add canvas dimensions metadata for accurate print scaling
      canvasData.canvasDimensions = getDimensionsInPixels();
      canvasData.physicalDimensions = labelDimensions;
      
      const dataMappings = extractDataMappings();
      const thumbnail = generateThumbnail();

      console.log('Saving template with canvas data:', canvasData);
      console.log('Canvas dimensions stored:', canvasData.canvasDimensions);
      console.log('Physical dimensions stored:', canvasData.physicalDimensions);

      const templateData = {
        name: templateName.trim(),
        description: templateDescription.trim(),
        category: templateCategory,
        is_default: currentTemplate?.is_default || false, // Preserve existing default status
        dimensions: labelDimensions,
        canvas_data: canvasData,
        data_mappings: dataMappings,
        thumbnail
      };

      if (currentTemplate?.id) {
        const { error } = await supabase
          .from('label_templates')
          .update(templateData)
          .eq('id', currentTemplate.id);

        if (error) throw error;
        toast.success('Template updated successfully');
      } else {
        const { data, error } = await supabase
          .from('label_templates')
          .insert([templateData])
          .select()
          .single();

        if (error) throw error;
        setCurrentTemplate(data);
        toast.success('Template saved successfully');
      }

      await loadTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const extractDataMappings = () => {
    if (!fabricCanvas) return {};

    const mappings: Record<string, any> = {};
    
    fabricCanvas.getObjects().forEach((obj, index) => {
      const objData = (obj as any).data;
      if (objData?.sourceType) {
        mappings[`element_${index}`] = {
          type: objData.sourceType,
          elementType: objData.elementType || 'text',
          position: { x: obj.left, y: obj.top },
          properties: {
            fontSize: obj instanceof Text ? obj.fontSize : undefined,
            fill: obj.fill,
            stroke: obj.stroke,
            strokeWidth: obj.strokeWidth,
            fontFamily: obj instanceof Text ? obj.fontFamily : undefined
          }
        };
      }
    });

    return mappings;
  };

  const setAsDefault = async (templateId: string) => {
    try {
      setLoading(true);
      
      // Clear current default
      await supabase
        .from('label_templates')
        .update({ is_default: false })
        .eq('is_default', true);

      // Set new default
      const { error } = await supabase
        .from('label_templates')
        .update({ is_default: true })
        .eq('id', templateId);

      if (error) throw error;
      toast.success('Default template updated');
      await loadTemplates();
    } catch (error) {
      console.error('Error setting default template:', error);
      toast.error('Failed to set default template');
    } finally {
      setLoading(false);
    }
  };

  const deleteTemplate = async (templateId: string) => {
    try {
      const { error } = await supabase
        .from('label_templates')
        .delete()
        .eq('id', templateId);

      if (error) throw error;
      toast.success('Template deleted');
      await loadTemplates();
      
      if (currentTemplate?.id === templateId) {
        setCurrentTemplate(null);
        setTemplateName("");
        setTemplateDescription("");
        setTemplateCategory("custom");
        fabricCanvas?.clear();
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const duplicateTemplate = async (template: LabelTemplate) => {
    try {
      const templateData = {
        name: `${template.name} (Copy)`,
        description: template.description,
        category: template.category,
        is_default: false,
        dimensions: template.dimensions,
        canvas_data: template.canvas_data,
        data_mappings: template.data_mappings,
        thumbnail: template.thumbnail
      };

      const { error } = await supabase
        .from('label_templates')
        .insert([templateData]);

      if (error) throw error;
      toast.success('Template duplicated');
      await loadTemplates();
    } catch (error) {
      console.error('Error duplicating template:', error);
      toast.error('Failed to duplicate template');
    }
  };

  const newTemplate = () => {
    setCurrentTemplate(null);
    setTemplateName("");
    setTemplateDescription("");
    setTemplateCategory("custom");
    fabricCanvas?.clear();
    if (fabricCanvas) {
      fabricCanvas.backgroundColor = "#ffffff";
      fabricCanvas.renderAll();
    }
    createDefaultElements();
    toast("New template created");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card/50 backdrop-blur supports-[backdrop-filter]:bg-card/50">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={onBack}>
              ← Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Template Designer</h1>
              <p className="text-sm text-muted-foreground">Create and manage label templates</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={currentTemplate ? "default" : "secondary"}>
              {currentTemplate ? `Editing: ${currentTemplate.name}` : "New Template"}
            </Badge>
            <Button variant="outline" onClick={newTemplate}>
              <Plus className="h-4 w-4 mr-2" />
              New
            </Button>
            <Button onClick={saveTemplate} disabled={saving || !templateName.trim()}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-120px)]">
        {/* Templates Sidebar */}
        <div className="w-80 border-r bg-card/50 backdrop-blur supports-[backdrop-filter]:bg-card/50">
          <div className="p-4 border-b">
            <h3 className="font-semibold mb-3">Templates</h3>
            <div className="space-y-2">
              <Input
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-8"
              />
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {TEMPLATE_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <ScrollArea className="h-[calc(100vh-240px)]">
            <div className="p-4 space-y-2">
              {loading ? (
                <div className="text-center text-sm text-muted-foreground py-8">
                  Loading templates...
                </div>
              ) : filteredTemplates.length === 0 ? (
                <div className="text-center text-sm text-muted-foreground py-8">
                  No templates found
                </div>
              ) : (
                filteredTemplates.map((template) => (
                  <Card 
                    key={template.id} 
                    className={`cursor-pointer transition-all hover:shadow-md ${
                      currentTemplate?.id === template.id ? 'ring-2 ring-primary' : ''
                    }`}
                    onClick={() => loadTemplate(template)}
                  >
                    <CardContent className="p-3">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-sm truncate">{template.name}</h4>
                            {template.is_default && (
                              <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                            )}
                          </div>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                duplicateTemplate(template);
                              }}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                            {!template.is_default && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAsDefault(template.id);
                                }}
                              >
                                <StarOff className="h-3 w-3" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0 text-destructive"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteTemplate(template.id);
                              }}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                        {template.description && (
                          <p className="text-xs text-muted-foreground truncate">
                            {template.description}
                          </p>
                        )}
                        <div className="flex items-center justify-between">
                          <Badge variant="outline" className="text-xs">
                            {TEMPLATE_CATEGORIES.find(c => c.value === template.category)?.label || 'Custom'}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {new Date(template.updated_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Main Design Area */}
        <div className="flex-1 flex flex-col">
          {/* Template Info */}
          <div className="p-4 border-b bg-card/30">
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="basic">Basic Info</TabsTrigger>
                <TabsTrigger value="dimensions">Dimensions</TabsTrigger>
                <TabsTrigger value="advanced">Advanced</TabsTrigger>
              </TabsList>
              <TabsContent value="basic" className="space-y-3 mt-3">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="templateName" className="text-xs">Template Name</Label>
                    <Input
                      id="templateName"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      placeholder="Enter template name"
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label htmlFor="templateCategory" className="text-xs">Category</Label>
                    <Select value={templateCategory} onValueChange={setTemplateCategory}>
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TEMPLATE_CATEGORIES.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="templateDescription" className="text-xs">Description</Label>
                    <Input
                      id="templateDescription"
                      value={templateDescription}
                      onChange={(e) => setTemplateDescription(e.target.value)}
                      placeholder="Optional description"
                      className="h-8"
                    />
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="dimensions" className="space-y-3 mt-3">
                <div className="grid grid-cols-4 gap-3 items-end">
                  <div>
                    <Label htmlFor="labelWidth" className="text-xs">Width</Label>
                    <Input
                      id="labelWidth"
                      type="number"
                      step="0.1"
                      value={labelDimensions.width}
                      onChange={(e) => setLabelDimensions(prev => ({ ...prev, width: parseFloat(e.target.value) || 0 }))}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label htmlFor="labelHeight" className="text-xs">Height</Label>
                    <Input
                      id="labelHeight"
                      type="number"
                      step="0.1"
                      value={labelDimensions.height}
                      onChange={(e) => setLabelDimensions(prev => ({ ...prev, height: parseFloat(e.target.value) || 0 }))}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label htmlFor="labelUnit" className="text-xs">Unit</Label>
                    <Select value={labelDimensions.unit} onValueChange={(value: 'in' | 'mm') => setLabelDimensions(prev => ({ ...prev, unit: value }))}>
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in">Inches</SelectItem>
                        <SelectItem value="mm">Millimeters</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (fabricCanvas) {
                          const pixelDimensions = getDimensionsInPixels();
                          fabricCanvas.setDimensions(pixelDimensions);
                          fabricCanvas.renderAll();
                          toast("Canvas resized to new dimensions");
                        }
                      }}
                      className="h-8"
                    >
                      Apply Size
                    </Button>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  Current size: {labelDimensions.width} × {labelDimensions.height} {labelDimensions.unit}
                  ({getDimensionsInPixels().width} × {getDimensionsInPixels().height} pixels)
                </div>
              </TabsContent>
              <TabsContent value="advanced" className="space-y-3 mt-3">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={undo}
                      disabled={undoStack.length === 0}
                    >
                      Undo
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={redo}
                      disabled={redoStack.length === 0}
                    >
                      Redo
                    </Button>
                  </div>
                  <Separator orientation="vertical" className="h-6" />
                  <div className="flex items-center gap-2">
                    <Button
                      variant={showGrid ? "default" : "outline"}
                      size="sm"
                      onClick={() => setShowGrid(!showGrid)}
                    >
                      <Grid className="h-3 w-3" />
                    </Button>
                    <Button variant="outline" size="sm">
                      <ZoomOut className="h-3 w-3" />
                    </Button>
                    <span className="text-xs">{Math.round(zoom * 100)}%</span>
                    <Button variant="outline" size="sm">
                      <ZoomIn className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          <div className="flex flex-1">
            {/* Canvas Area */}
            <div className="flex-1 p-4">
              <Card className="h-full">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Design Canvas</CardTitle>
                    <Button variant="outline" size="sm" onClick={updateTemplatePreview}>
                      <Eye className="h-3 w-3 mr-1" />
                      Preview
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-4 h-[calc(100%-80px)]">
                  <div className="h-full flex items-center justify-center bg-gray-50 rounded">
                    <canvas 
                      ref={canvasRef} 
                      className="border border-border shadow-lg" 
                      style={{ 
                        transform: `scale(${zoom})`,
                        transformOrigin: 'center'
                      }}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tools Panel */}
            <div className="w-80 p-4">
              <Card className="h-full">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Tools & Properties</CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <ScrollArea className="h-[calc(100vh-320px)]">
                    <div className="space-y-4">
                      {/* Add Elements */}
                      <div>
                        <h4 className="font-medium mb-2 text-sm">Add Elements</h4>
                        <div className="grid grid-cols-2 gap-2">
                          <Button size="sm" onClick={addTextElement} className="flex items-center gap-1">
                            <Type className="h-3 w-3" />
                            Text
                          </Button>
                          <Button size="sm" onClick={addRectangle} className="flex items-center gap-1">
                            <Square className="h-3 w-3" />
                            Rectangle
                          </Button>
                          <Button size="sm" onClick={addCircle} className="flex items-center gap-1">
                            <CircleIcon className="h-3 w-3" />
                            Circle
                          </Button>
                          <Button size="sm" onClick={addLine} className="flex items-center gap-1">
                            <Minus className="h-3 w-3" />
                            Line
                          </Button>
                          <Button size="sm" onClick={addBarcodeElement} className="flex items-center gap-1 col-span-2">
                            <BarChart3 className="h-3 w-3" />
                            Barcode
                          </Button>
                        </div>
                      </div>

                      {selectedObject && (
                        <>
                          <Separator />
                          
                          {/* Element Actions */}
                          <div>
                            <h4 className="font-medium mb-2 text-sm">Element Actions</h4>
                            <div className="grid grid-cols-2 gap-2">
                              <Button 
                                size="sm" 
                                onClick={duplicateSelectedObject} 
                                variant="outline"
                                className="flex items-center gap-1"
                              >
                                <Copy className="h-3 w-3" />
                                Duplicate
                              </Button>
                              <Button 
                                size="sm" 
                                onClick={deleteSelectedElement} 
                                variant="destructive" 
                                className="flex items-center gap-1"
                              >
                                <Trash2 className="h-3 w-3" />
                                Delete
                              </Button>
                            </div>
                          </div>

                          <Separator />

                          {/* Properties Panel */}
                          <div>
                            <h4 className="font-medium mb-2 text-sm">Properties</h4>
                            
                            {selectedObject instanceof Text && (
                              <div className="space-y-3">
                                <div>
                                  <Label className="text-xs">Data Source</Label>
                                  <Select
                                    value={(selectedObject as any).data?.sourceType || "STATIC"}
                                    onValueChange={(value) => updateSelectedObjectProperty('sourceType', value)}
                                  >
                                    <SelectTrigger className="h-8">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {DATA_SOURCES.map((source) => (
                                        <SelectItem key={source.value} value={source.value}>
                                          {source.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>

                                {(selectedObject as any).data?.sourceType === 'STATIC' && (
                                  <div>
                                    <Label className="text-xs">Text Content</Label>
                                    <Input
                                      value={selectedObject.text || ''}
                                      onChange={(e) => updateSelectedObjectProperty('text', e.target.value)}
                                      className="h-8"
                                    />
                                  </div>
                                )}

                                 <div>
                                   <Label className="text-xs">Font Family</Label>
                                   <Select
                                     value={selectedObject.fontFamily || 'Arial'}
                                     onValueChange={(value) => updateSelectedObjectProperty('fontFamily', value)}
                                   >
                                     <SelectTrigger className="h-8">
                                       <SelectValue />
                                     </SelectTrigger>
                                     <SelectContent>
                                       <SelectItem value="Arial">Arial</SelectItem>
                                       <SelectItem value="Helvetica">Helvetica</SelectItem>
                                       <SelectItem value="Times New Roman">Times New Roman</SelectItem>
                                       <SelectItem value="Georgia">Georgia</SelectItem>
                                       <SelectItem value="Verdana">Verdana</SelectItem>
                                       <SelectItem value="Courier New">Courier New</SelectItem>
                                     </SelectContent>
                                   </Select>
                                 </div>

                                 <div>
                                   <Label className="text-xs">Text Formatting</Label>
                                   <div className="flex gap-1 mt-1">
                                     <Button
                                       variant={selectedObject.fontWeight === 'bold' ? 'default' : 'outline'}
                                       size="sm"
                                       onClick={() => updateSelectedObjectProperty('fontWeight', 
                                         selectedObject.fontWeight === 'bold' ? 'normal' : 'bold'
                                       )}
                                       className="h-8 w-8 p-0"
                                     >
                                       <Bold className="w-3 h-3" />
                                     </Button>
                                     <Button
                                       variant={selectedObject.fontStyle === 'italic' ? 'default' : 'outline'}
                                       size="sm"
                                       onClick={() => updateSelectedObjectProperty('fontStyle', 
                                         selectedObject.fontStyle === 'italic' ? 'normal' : 'italic'
                                       )}
                                       className="h-8 w-8 p-0"
                                     >
                                       <Italic className="w-3 h-3" />
                                     </Button>
                                     <Button
                                       variant={selectedObject.underline ? 'default' : 'outline'}
                                       size="sm"
                                       onClick={() => updateSelectedObjectProperty('underline', !selectedObject.underline)}
                                       className="h-8 w-8 p-0"
                                     >
                                       <Underline className="w-3 h-3" />
                                     </Button>
                                   </div>
                                 </div>

                                 <div>
                                   <Label className="text-xs">Font Size</Label>
                                   <Input
                                     type="number"
                                     min="8"
                                     max="72"
                                     value={selectedObject.fontSize || 14}
                                     onChange={(e) => updateSelectedObjectProperty('fontSize', e.target.value)}
                                     className="h-8"
                                   />
                                 </div>

                                 <div>
                                   <Label className="text-xs">Text Color</Label>
                                   <Input
                                     type="color"
                                     value={typeof selectedObject.fill === 'string' ? selectedObject.fill : '#000000'}
                                     onChange={(e) => updateSelectedObjectProperty('fill', e.target.value)}
                                     className="h-8"
                                   />
                                 </div>

                                <div>
                                  <Label className="text-xs">Text Outline</Label>
                                  <div className="grid grid-cols-3 gap-1">
                                    <input
                                      type="color"
                                      value={typeof selectedObject.stroke === 'string' ? selectedObject.stroke : '#000000'}
                                      onChange={(e) => updateSelectedObjectProperty('stroke', e.target.value)}
                                      className="w-full h-8 border rounded cursor-pointer"
                                      title="Outline Color"
                                    />
                                    <Input
                                      type="number"
                                      min="0"
                                      max="10"
                                      step="0.1"
                                      value={selectedObject.strokeWidth || 0}
                                      onChange={(e) => updateSelectedObjectProperty('strokeWidth', e.target.value)}
                                      placeholder="Width"
                                      className="h-8 text-xs"
                                    />
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        updateSelectedObjectProperty('stroke', null);
                                        updateSelectedObjectProperty('strokeWidth', 0);
                                      }}
                                      className="h-8 text-xs"
                                    >
                                      Clear
                                    </Button>
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Outline auto-scales with font size
                                  </p>
                                </div>
                              </div>
                            )}

                            {selectedObject instanceof Rect && (
                              <div className="space-y-3">
                                <div>
                                  <Label className="text-xs">Fill Color</Label>
                                  <Input
                                    type="color"
                                    value={typeof selectedObject.fill === 'string' ? selectedObject.fill : '#ffffff'}
                                    onChange={(e) => updateSelectedObjectProperty('fill', e.target.value)}
                                    className="h-8"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Border Color</Label>
                                  <Input
                                    type="color"
                                    value={typeof selectedObject.stroke === 'string' ? selectedObject.stroke : '#000000'}
                                    onChange={(e) => updateSelectedObjectProperty('stroke', e.target.value)}
                                    className="h-8"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Border Width</Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={selectedObject.strokeWidth || 1}
                                    onChange={(e) => updateSelectedObjectProperty('strokeWidth', e.target.value)}
                                    className="h-8"
                                  />
                                </div>
                              </div>
                            )}

                            {selectedObject instanceof Circle && (
                              <div className="space-y-3">
                                <div>
                                  <Label className="text-xs">Fill Color</Label>
                                  <Input
                                    type="color"
                                    value={typeof selectedObject.fill === 'string' ? selectedObject.fill : '#ffffff'}
                                    onChange={(e) => updateSelectedObjectProperty('fill', e.target.value)}
                                    className="h-8"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Border Color</Label>
                                  <Input
                                    type="color"
                                    value={typeof selectedObject.stroke === 'string' ? selectedObject.stroke : '#000000'}
                                    onChange={(e) => updateSelectedObjectProperty('stroke', e.target.value)}
                                    className="h-8"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Border Width</Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={selectedObject.strokeWidth || 1}
                                    onChange={(e) => updateSelectedObjectProperty('strokeWidth', e.target.value)}
                                    className="h-8"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Radius</Label>
                                  <Input
                                    type="number"
                                    min="1"
                                    value={selectedObject.radius || 30}
                                    onChange={(e) => updateSelectedObjectProperty('radius', e.target.value)}
                                    className="h-8"
                                  />
                                </div>
                              </div>
                            )}

                            {selectedObject instanceof Line && (
                              <div className="space-y-3">
                                <div>
                                  <Label className="text-xs">Line Color</Label>
                                  <Input
                                    type="color"
                                    value={typeof selectedObject.stroke === 'string' ? selectedObject.stroke : '#000000'}
                                    onChange={(e) => updateSelectedObjectProperty('stroke', e.target.value)}
                                    className="h-8"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Line Width</Label>
                                  <Input
                                    type="number"
                                    min="1"
                                    value={selectedObject.strokeWidth || 2}
                                    onChange={(e) => updateSelectedObjectProperty('strokeWidth', e.target.value)}
                                    className="h-8"
                                  />
                                </div>
                              </div>
                            )}

                            {/* Alignment Controls */}
                            <div>
                              <Label className="text-xs">Alignment</Label>
                              <div className="grid grid-cols-3 gap-1">
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={() => alignObject('left')}
                                  className="h-8"
                                >
                                  <AlignLeft className="h-3 w-3" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={() => alignObject('center')}
                                  className="h-8"
                                >
                                  <AlignCenter className="h-3 w-3" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={() => alignObject('right')}
                                  className="h-8"
                                >
                                  <AlignRight className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>

                            {/* Position Controls */}
                            <div>
                              <Label className="text-xs">Position</Label>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <Label className="text-xs text-muted-foreground">X</Label>
                                  <Input
                                    type="number"
                                    value={Math.round(selectedObject.left || 0)}
                                    onChange={(e) => updateSelectedObjectProperty('left', parseInt(e.target.value))}
                                    className="h-8"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs text-muted-foreground">Y</Label>
                                  <Input
                                    type="number"
                                    value={Math.round(selectedObject.top || 0)}
                                    onChange={(e) => updateSelectedObjectProperty('top', parseInt(e.target.value))}
                                    className="h-8"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
